import dayjs from 'dayjs';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
  LoggerFactory, EVENT, FEATURE, DOMAIN,
} from 'Services/Logger';

dayjs.locale('es');
dayjs.extend(utc);
dayjs.extend(timezone);

const Logger = LoggerFactory.getLogger(DOMAIN.AMON);

export const isNumber = value => (
  !Number.isNaN(value)
  && !Number.isNaN(parseFloat(value))
);

export const parseMXToNumber = (number) => {
  if (typeof number === 'number') {
    return number;
  }

  if (!number || typeof number !== 'string') {
    return null;
  }

  const formattedNumber = number.replace(/[^\d.]/g, '');
  if (Number.isNaN(formattedNumber)) {
    return null;
  }

  return Number(formattedNumber);
};

export const formatNumberToMX = (number) => {
  if (!isNumber(number)) {
    return null;
  }

  const value = Number(number);
  const formattedNumber = value.toFixed(2).toString().match(/^-?\d+(?:\.\d{0,2})?/)[0];

  return formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const numberToMXN = (number) => {
  if (!number || Number.isNaN(Number(number))) return '$0.00';

  return `$${formatNumberToMX(number)}`;
};

export const numberToMXNWithoutDecimal = (
  number,
  withMonetarySymbol = true,
) => {
  const monetarySymbol = withMonetarySymbol ? '$' : '';
  if (!number || Number.isNaN(Number(number))) return `${monetarySymbol}0`;

  return `${monetarySymbol}${formatNumberToMX(number)}`.split('.')[0];
};

export const parseThresholdRange = (thresholdRange) => {
  const zero = numberToMXN(0);
  const thresholds = thresholdRange?.split(',') ?? [];
  const [min = zero, max = zero] = thresholds.map(threshold => numberToMXN(threshold.trim()));
  return { max, min };
};

// FIXME: Refactor code to use clearly the timezone parser. The FIXMEs in this function make
//  developers from different timezones to execute the parser according to the America/Mexico_City
//  timezone instead of their timezone.
const parseDate = (date, format, locale = 'es') => {
  const objectDate = dayjs(date);

  // FIXME: Handle the scenario when we send and epoch instead a string or date.
  if (typeof date === 'number') {
    return objectDate.tz('America/Mexico_City').locale(locale).format(format);
  }

  // FIXME: Use a proper validator
  const simpleYearFormatMatcher = /^[1-2][0-9][0-9][0-9]-[0-1][0-9]-[0-3][0-9]?/;
  const ISO_8601_FULL = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/i;

  // FIXME: Some legacy endpoints returns a format YYYY-MM-DD, this match will parse
  //  the string to the timezone because doesn't include the timezone format.
  if (typeof date === 'string' && date.match(simpleYearFormatMatcher)) {
    if (date.match(ISO_8601_FULL)) {
      return dayjs.tz(new Date(date), 'America/Mexico_City').locale(locale).format(format);
    }
    return dayjs.tz(date, 'America/Mexico_City').locale(locale).format(format);
  }

  return objectDate.locale(locale).format(format);
};

export const simpleDate = (date, format = 'DD MMM. YYYY') => {
  const result = parseDate(date, format).toLowerCase();
  if (result === 'invalid date') {
    Logger.error('LoanItemCard: invalid date', `${FEATURE.PROFILE}.${EVENT.SHOW_LOAN_ITEM_CARD}`);
  }
  return result;
};

export const simpleDateNoYear = (date, format = 'DD MMM') => {
  const result = parseDate(date, format).toLowerCase();
  if (result === 'invalid date') {
    Logger.error('LoanItemCard: invalid date', `${FEATURE.PROFILE}.${EVENT.SHOW_LOAN_ITEM_CARD}`);
  }
  return result;
};

export const parseLongDate = date => parseDate(date, 'DD [de] MMMM YYYY').toLowerCase();

export const parseYearMonthDate = date => parseDate(date, 'YYYY/MM/DD');

export const fullDate = date => parseDate(date, 'DD [de] MMMM [de] YYYY').toLowerCase();

export const reduceMonths = (months = 0, customFormat = 'YYYY-MM-DD', date = null) => {
  const today = date ? dayjs(date) : dayjs();
  return today.subtract(months, 'month').format(customFormat);
};

export const compareDates = (date1, date2) => {
  const parsedDate1 = dayjs(date1);
  const parsedDate2 = dayjs(date2);

  if (parsedDate1 > parsedDate2) {
    return 1;
  }
  if (parsedDate1 < parsedDate2) {
    return -1;
  }
  return 0;
};

export const parseRoute = (url, key, value) => {
  const keyRegex = new RegExp(`\\/:${key}\\?*`);

  return url.replace(keyRegex, value ? `/${value}` : '');
};

const numberToPercent = (input, decimalPlaces) => {
  const number = Number.isNaN(input) ? 0 : +input;
  return `${formatNumberToMX(number.toFixed(decimalPlaces))}%`;
};

export const formatNumberToPercentage = (amount, decimalPlaces) => (
  numberToPercent(amount * 100, decimalPlaces)
);

export const formatPhoneNumber = (num, separator = ' ') => num
  .toString() // Ensure we are working with strings
  .replace(/[^0-9]/g, '') // Strip all non-numbers
  .match(/(\d{0,3})(\d{0,3})?(\d{0,4})?/) // Split in groups of 3-3-4 numbers
  .slice(1, 4) // Get the groups
  .filter(Boolean) // Exclude empty groups
  .join(separator); // Join using the specified separator

/**
 * Converts to String and strips any non-numeric character.
 *
 * @param {number|string} num Number to strip
 *
 * @returns A script with only numeric characters or empty string
 */
export const extractNumbers = (num = '') => {
  const matches = num.toString().match(/\d/g) ?? [];
  return matches.join('');
};

const formatInGroups = (text, groupLength = 4, separator = ' ') => {
  if (!text) return '';
  const regex = new RegExp(`.{1,${groupLength}}`, 'g');
  const groups = text.match(regex); // Split in groups
  if (!groups) return '';
  return groups
    .join(separator); // Join using the specified separator
};

export const formatCardOrAccount = (num, separator = ' ') => {
  const onlyNumbers = extractNumbers(num);
  const groupLength = onlyNumbers.length <= 16 ? 4 : 3;

  return formatInGroups(extractNumbers(num).substring(0, 18), groupLength, separator);
};

export const getReferenceFromSantander = (santanderReference) => {
  const referenceData = santanderReference.split('_');
  if (referenceData.length === 3) {
    return '/';
  }
  return `/${referenceData[0]}/`;
};
